/**
 * Aggregates all hexagon cells into a single summary with the *same shape as a
 * cell* (`{ stats, detections }`), so one presentational component can render
 * either a selected cell or the whole-collection summary.
 *
 * Why aggregate client-side: unlike the model-based hexagons (which return a
 * `metadata.total` block), the collection endpoint returns only per-cell
 * features. So the "total" is summed here from the cells.
 *
 * The aggregation mirrors `deriveCellStats`' per-class math one level up:
 *   - per class: sum count + sum_object_area, count-weight mean_height, sum
 *     estimated_stems levels (present only if some cell reported them);
 *   - top-level stats: delegated to `deriveCellStats` on the aggregated
 *     detections, with `area` = the total ground area (Σ cell areas), so density
 *     and per-ha stems reflect the whole collection.
 *
 * @module collectionHexagonsSummary
 */

import { deriveCellStats } from './hexagonCellStats';

/**
 * @param {import('./collectionHexagonsToCells').HexagonCell[]} cells
 * @returns {{ stats: object, detections: object, cellCount: number } | null}
 *   null when there are no cells.
 */
export const collectionHexagonsSummary = cells => {
	if (!cells?.length) return null;

	// Per-class accumulators. Height is count-weighted; estimated_stems is summed
	// only across cells that actually reported it (stays absent otherwise, so the
	// downstream enrichment treats the class as "no stem data").
	const acc = {};
	let totalArea = 0;

	for (const cell of cells) {
		totalArea += cell.stats?.area ?? 0;

		for (const [className, det] of Object.entries(cell.detections ?? {})) {
			const entry = (acc[className] ??= {
				count: 0,
				sum_object_area: 0,
				heightWeighted: 0,
				heightWeight: 0,
				stems: null,
			});

			const count = det.count ?? 0;
			entry.count += count;
			entry.sum_object_area += det.sum_object_area ?? 0;

			if (det.mean_height != null && count > 0) {
				entry.heightWeighted += det.mean_height * count;
				entry.heightWeight += count;
			}

			if (det.estimated_stems) {
				entry.stems ??= { low: 0, medium: 0, high: 0 };
				entry.stems.low += det.estimated_stems.low ?? 0;
				entry.stems.medium += det.estimated_stems.medium ?? 0;
				entry.stems.high += det.estimated_stems.high ?? 0;
			}
		}
	}

	// Finalize per-class detections into the same shape a cell carries.
	const detections = {};
	for (const [className, entry] of Object.entries(acc)) {
		detections[className] = {
			count: entry.count,
			mean_height: entry.heightWeight > 0 ? entry.heightWeighted / entry.heightWeight : null,
			sum_object_area: entry.sum_object_area,
			...(entry.stems ? { estimated_stems: entry.stems } : {}),
		};
	}

	return {
		stats: deriveCellStats(detections, totalArea),
		detections,
		cellCount: cells.length,
	};
};

export default collectionHexagonsSummary;
